'use client';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { BypassBlock } from '@packages/shared/src/ui/BypassBlock/BypassBlock';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { DialogHeader } from '../Dialog/DialogHeader/DialogHeader.client';
import { KimRatingDialogContent } from './KimRating.dialogContent';
import { messages } from './KimRating.messages';
import type { KimRatingProps, RatingIndex, RatingValue } from './KimRating.types';
/**
 * Renders the KimRating component to pickup feedback from customers
 *
 * @param props - The component props consist of pageType (storefront | search | detailpage) and
 * templateParams that override the default query params against the kim backend
 *
 * @returns The rendered customer feedback component.
 */
export const KimRating = ({ pageType, templateParams }: KimRatingProps) => {
  const { getCookies } = useCookies();
  const { appView } = getCookies();
  const [ratingValue, setRatingValue] = useState<RatingIndex | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const { formatMessage } = useIntl();
  const config = useConfig();
  const buttonRef = useRef<HTMLButtonElement>(null);

  // A11Y-411: focus the button when the ratingValue changes
  useEffect(() => {
    if (ratingValue !== null) {
      buttonRef.current?.focus();
    }
  }, [ratingValue]);

  // "kim "is renamed in config to "customerCentricFeedback"
  if (appView === 'true' || !config.customerCentricFeedback.enabled) {
    return null;
  }

  return (
    <BypassBlock buttonText={formatMessage(messages.bypassLabel)}>
      <div className="flex-center flex flex-col text-center">
        {/* position relative is needed for the speech bubble to be displayed above the colored box */}
        <div className="relative">
          <span className="icon-emp-template-rating mt-1.5 size-[5rem]" />
        </div>
        {/* make this margin-padding-switch to let the speech bubble overlap the colored container */}
        <div className="bg-color-grey-light -mt-6 flex w-full flex-col items-center px-6 pt-6">
          <p className="text-size-body-xs">{formatMessage(messages.helpUs)}</p>
          <p className="text-size-body-base pt-1">
            {pageType === 'storefront' && formatMessage(messages.questionStorefront)}
            {pageType === 'detailpage' && formatMessage(messages.questionDetailpage)}
            {pageType === 'search' && formatMessage(messages.questionSearch)}
            {pageType === 'wishlist' && formatMessage(messages.questionWishlist)}
          </p>
          <div className="inline-flex max-w-[19.5rem] flex-wrap justify-center pt-3.5">
            {[
              formatMessage(messages.ratingVeryUnsatisfied),
              formatMessage(messages.ratingUnsatisfied),
              formatMessage(messages.ratingNor),
              formatMessage(messages.ratingSatisfied),
              formatMessage(messages.ratingVerySatisfied),
            ].map((text, index) => (
              <Button
                key={text}
                size="base"
                className="flex flex-1 flex-col items-center justify-start border-none bg-transparent p-[0.3rem] text-inherit no-underline"
                onClick={(e) => {
                  e.preventDefault();
                  setRatingValue(index as RatingIndex);
                }}
              >
                {/* do not use just 'icon-emp-star' here as we have to style the stroke and the fill color */}
                <span
                  className={clsx(
                    'stroke-color-text-dark size-[1.7rem]',
                    ratingValue === null || ratingValue < index
                      ? 'fill-color-text-light'
                      : 'fill-color-kim',
                  )}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
                    <path
                      d="M16 25.173 6.42 31l2.487-11.071L.5 12.459l11.116-1.015L16 1l4.384 10.444L31.5 12.459l-8.407 7.47L25.58 31z"
                      className="path-1"
                    />
                  </svg>
                </span>
                <p className="mt-1 text-[0.6rem] leading-2 break-normal">{text}</p>
              </Button>
            ))}
          </div>
          <div className={clsx('pt-5 pb-2.5', ratingValue === null ? 'invisible' : 'visible')}>
            <Dialog
              isOpen={modalOpen}
              onClose={() => setModalOpen(false)}
              className="w-full max-w-[600px]"
            >
              <DialogHeader onClose={() => setModalOpen(false)}>
                {formatMessage(messages.helpUs)}
              </DialogHeader>
              <DialogContent className="p-0">
                {
                  // make the dialog content conditional to dynamically load further complex hooks
                  ratingValue !== null && (
                    <KimRatingDialogContent
                      pageType={pageType}
                      ratingValue={(ratingValue + 1) as RatingValue}
                      templateParams={templateParams}
                    />
                  )
                }
              </DialogContent>
            </Dialog>
            <Button
              ref={buttonRef}
              variant="color-button"
              onClick={() => setModalOpen(true)}
              className="min-w-[12rem]"
            >
              {formatMessage(messages.ratingOnwards)}
            </Button>
            <div>
              <span className="icon-emp-radio-selected-24 size-1" />
              <span className="icon-emp-radio-24 size-1" />
            </div>
          </div>
        </div>
      </div>
    </BypassBlock>
  );
};
